/**
 * Shared frontend validators
 *
 * Form-style validators return `ValidationResult` with a Russian error message.
 * Primitive validators return `boolean`.
 */

export type ValidationResult = {
	valid: boolean;
	error?: string;
};

// ============================================================================
// Form-style validators (with Russian error messages)
// ============================================================================

/**
 * Validate email format
 */
export function validateEmail(email: string): ValidationResult {
	const trimmed = email.trim();

	if (trimmed.length === 0) {
		return { valid: false, error: "Email обязателен" };
	}

	const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
	if (!emailRegex.test(trimmed)) {
		return { valid: false, error: "Введите корректный email" };
	}

	return { valid: true };
}

/**
 * Validate phone number format
 * Supports international format with +, digits, spaces, brackets, dashes
 */
export function validatePhone(phone: string): ValidationResult {
	const trimmed = phone.trim();

	if (trimmed.length === 0) {
		return { valid: false, error: "Телефон обязателен" };
	}

	const phoneRegex = /^[\d\s+\-()]+$/;
	if (!phoneRegex.test(trimmed)) {
		return { valid: false, error: "Введите корректный номер телефона" };
	}

	const digitCount = trimmed.replace(/\D/g, "").length;
	if (digitCount < 10) {
		return { valid: false, error: "Номер телефона слишком короткий" };
	}

	return { valid: true };
}

/**
 * Validate phone number in masked format +7 (XXX) XXX-XX-XX
 */
export function validateMaskedPhone(phone: string): ValidationResult {
	const trimmed = phone.trim();

	if (trimmed.length === 0) {
		return { valid: false, error: "Телефон обязателен" };
	}

	const maskRegex = /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/;
	if (!maskRegex.test(trimmed)) {
		return { valid: false, error: "Введите телефон полностью в формате +7 (XXX) XXX-XX-XX" };
	}

	return { valid: true };
}

/**
 * Validate password strength
 * Basic requirements: minimum length
 */
export function validatePassword(password: string): ValidationResult {
	if (password.length === 0) {
		return { valid: false, error: "Пароль обязателен" };
	}

	if (password.length < 8) {
		return { valid: false, error: "Пароль должен содержать минимум 8 символов" };
	}

	return { valid: true };
}

/**
 * Validate name (first/last name)
 */
export function validateName(name: string): ValidationResult {
	const trimmed = name.trim();

	if (trimmed.length === 0) {
		return { valid: false, error: "Имя обязательно" };
	}

	if (trimmed.length < 2) {
		return { valid: false, error: "Имя должно содержать минимум 2 символа" };
	}

	return { valid: true };
}

// ============================================================================
// Form aggregation
// ============================================================================

export interface FormValues {
	email?: string;
	phone?: string;
	password?: string;
	name?: string;
}

export interface FormValidationResult {
	valid: boolean;
	errors: Record<string, string>;
}

export function validateForm(values: FormValues): FormValidationResult {
	const errors: Record<string, string> = {};

	if (values.email) {
		const emailResult = validateEmail(values.email);
		if (!emailResult.valid && emailResult.error) {
			errors.email = emailResult.error;
		}
	}

	if (values.phone) {
		const phoneResult = validatePhone(values.phone);
		if (!phoneResult.valid && phoneResult.error) {
			errors.phone = phoneResult.error;
		}
	}

	if (values.password) {
		const passwordResult = validatePassword(values.password);
		if (!passwordResult.valid && passwordResult.error) {
			errors.password = passwordResult.error;
		}
	}

	if (values.name) {
		const nameResult = validateName(values.name);
		if (!nameResult.valid && nameResult.error) {
			errors.name = nameResult.error;
		}
	}

	return {
		valid: Object.keys(errors).length === 0,
		errors,
	};
}

// ============================================================================
// Primitive validators (boolean return)
// ============================================================================

/**
 * Validate INN format (10 or 12 digits)
 */
export function validateInn(inn: string): boolean {
	const digits = inn.replace(/\D/g, '');
	return digits.length === 10 || digits.length === 12;
}

/**
 * Validate KPP format (9 digits)
 */
export function validateKpp(kpp: string): boolean {
	const digits = kpp.replace(/\D/g, '');
	return digits.length === 9;
}

/**
 * Normalize phone (remove non-digits, return digits-only if ≥10 digits, else empty)
 *
 * Note: This is a "strict" normalizer that returns only digits. For UI input
 * formatting (preserving the leading `+`), see `normalizePhoneInput` in `shared/utils`.
 */
export function normalizePhone(phone: string): string {
	const digits = phone.replace(/\D/g, '');
	return digits.length >= 10 ? digits : '';
}
